import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Breadcrumbs, PageHeader } from '~/components/data';
import { useToast } from '~/components/feedback';
import { Button, DatePicker, Input } from '~/components/primitives';
import { type BudgetUpdateBody, fetchBudget, updateBudget } from '~/lib/api/budgets';
import { ApiRequestError } from '~/lib/api/client';
import { renderApiErrorMessage } from '~/lib/api/error-renderer';
import shared from './_auth._shared.module.css';

/**
 * EFE-10 — Budget header edit (name + project_tag + period). Draft only.
 * Lines edit — отдельный flow через budget detail page (PUT replace или line CRUD).
 */
export const Route = createFileRoute('/_auth/budgets/$id/edit')({
  component: BudgetEdit,
});

function BudgetEdit(): JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['budgets', id],
    queryFn: () => fetchBudget(id),
  });

  const [form, setForm] = useState({
    name_ru: '',
    project_tag: '',
    period_from: '',
    period_to: '',
  });
  const [primed, setPrimed] = useState(false);

  useEffect(() => {
    if (query.data && !primed) {
      const b = query.data;
      setForm({
        name_ru: b.name_ru ?? '',
        project_tag: b.project_tag ?? '',
        period_from: b.period_from ?? '',
        period_to: b.period_to ?? '',
      });
      setPrimed(true);
    }
  }, [query.data, primed]);

  const updateMut = useMutation({
    // Ошибки форма показывает сама (409 — отдельным предупреждением), без глобального тоста.
    meta: { suppressErrorToast: true },
    mutationFn: () => {
      const b = query.data;
      const body: BudgetUpdateBody = {
        name_ru: form.name_ru,
        project_tag: form.project_tag || null,
        period_from: form.period_from,
        period_to: form.period_to,
        // EFE-14: optimistic concurrency.
        expected_updated_at: b?.updated_at ?? b?.created_at,
      };
      return updateBudget(id, body);
    },
    onSuccess: () => {
      toast.push({ variant: 'success', title: 'Бюджет обновлён' });
      void qc.invalidateQueries({ queryKey: ['budgets', id] });
      void qc.invalidateQueries({ queryKey: ['budgets'] });
      void navigate({ to: '/budgets/$id', params: { id } });
    },
    onError: (err: unknown) => {
      /*
       * F11 / PLM-4 (пак `tails-closeout`, B17 (а)) — бюджет изменил кто-то другой
       * после того, как форма его загрузила: сервер отвечает 409 `budget.stale`.
       * Форма не перезаписывает чужую правку молча — говорит об этом и перечитывает
       * бюджет, введённое остаётся в полях.
       */
      if (err instanceof ApiRequestError && err.status === 409 && err.code === 'budget.stale') {
        toast.push({
          variant: 'warning',
          title: m.budget_stale_title(),
          description: m.budget_stale_body(),
        });
        void qc.invalidateQueries({ queryKey: ['budgets', id] });
        return;
      }
      toast.push({
        variant: 'error',
        title: 'Не удалось обновить бюджет',
        description: renderApiErrorMessage(err),
      });
    },
  });

  if (query.isLoading) return <div>…</div>;
  if (!query.data) return <div>Not found</div>;

  const b = query.data;
  const readOnly = b.state !== 'draft';

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.phase2_budgets_title(), to: '/budgets' },
              { label: b.name_ru, to: `/budgets/${id}` },
              { label: m.common_button_edit() },
            ]}
          />
        }
        title={m.budget_edit_title()}
      />
      {readOnly && (
        <div className={[shared.inlineAlert, shared.inlineAlertWarn].join(' ')} role="status">
          {m.error_entity_not_editable({
            current_state: b.state,
            editable_states: 'draft',
          })}
        </div>
      )}
      <div className={shared.formColumn480}>
        <Input
          label={m.budget_field_name()}
          required
          value={form.name_ru}
          onChange={(e) => setForm({ ...form, name_ru: e.target.value })}
          disabled={readOnly}
        />
        <Input
          label={m.budget_field_project_tag()}
          value={form.project_tag}
          onChange={(e) => setForm({ ...form, project_tag: e.target.value })}
          disabled={readOnly}
        />
        <div className={shared.formGrid2}>
          <DatePicker
            label={m.budget_field_period_from()}
            value={form.period_from || null}
            onChange={(next) => setForm({ ...form, period_from: next ?? '' })}
            disabled={readOnly}
          />
          <DatePicker
            label={m.budget_field_period_to()}
            value={form.period_to || null}
            onChange={(next) => setForm({ ...form, period_to: next ?? '' })}
            disabled={readOnly}
          />
        </div>
      </div>
      <div className={shared.stickyFooter}>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate({ to: '/budgets/$id', params: { id } })}
        >
          {m.common_button_cancel()}
        </Button>
        <Button
          variant="primary"
          onClick={() => updateMut.mutate()}
          loading={updateMut.isPending}
          disabled={readOnly || !form.name_ru}
        >
          {m.common_button_save()}
        </Button>
      </div>
    </div>
  );
}
